import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Login from './components/Login';
import Register from './components/Register';
import VerifyEmail from './components/VerifyEmail';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import DashboardLayout from './components/DashboardLayout';
import SeriesDetailsMUI from './components/SeriesDetailsMUI';
import SeriesListAG from './components/SeriesListAG';
import Contests from './components/Contests';
import Users from './components/Users';
import Profile from './components/Profile';
import PlayerScores from './components/PlayerScores';
import Tracker from './components/Tracker';
import Matches from './components/Matches';
import PlanRulesConfig from './components/PlanRulesConfig';
import ScoringRulesConfig from './components/ScoringRulesConfig';
import ContestRulesConfig from './components/ContestRulesConfig';
import NotificationsForm from './components/NotificationsForm';
import AppSettings from './components/AppSettings';
import ContestDetails from './components/ContestDetails';
import FeedbackList from './components/FeedbackList';
import FeedbackDetails from './components/FeedbackDetails';
import SystemDocs from './components/SystemDocs';
import SchedulerJobs from './components/SchedulerJobs';
import ScoringHealth from './components/ScoringHealth';
import Auctions from './components/Auctions';
import AuctionDetails from './components/AuctionDetails';

const ProtectedRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const PublicRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Root - redirect to login (PublicRoute handles auth redirect to dashboard) */}
            <Route path="/" element={<PublicRoute><Login /></PublicRoute>} />

            {/* Public Routes - Authentication */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected Routes - Dashboard */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="series" element={<SeriesListAG />} />
              <Route path="series/:seriesId" element={<SeriesDetailsMUI />} />
              <Route path="series/:seriesId/player-scores" element={<PlayerScores />} />
              <Route path="matches" element={<Matches />} />
              <Route path="contests" element={<Contests />} />
              <Route path="contests/:contestId" element={<ContestDetails />} />
              <Route path="auctions" element={<Auctions />} />
              <Route path="auctions/:auctionId" element={<AuctionDetails />} />
              <Route path="users" element={<Users />} />
              <Route path="profile" element={<Profile />} />
              <Route path="tracker" element={<Tracker />} />
              <Route path="plan-rules" element={<PlanRulesConfig />} />
              <Route path="scoring-rules" element={<ScoringRulesConfig />} />
              <Route path="contest-rules" element={<ContestRulesConfig />} />
              <Route path="notifications" element={<NotificationsForm />} />
              <Route path="app-settings" element={<AppSettings />} />
              <Route path="feedback" element={<FeedbackList />} />
              <Route path="feedback/:feedbackId" element={<FeedbackDetails />} />
              <Route path="scheduler-jobs" element={<SchedulerJobs />} />
              <Route path="scoring-health" element={<ScoringHealth />} />
              <Route path="system-docs" element={<SystemDocs />} />
            </Route>

            {/* Catch-all - redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
